<template>
    <div>
        <limitless-page-header></limitless-page-header>

        <div class="content">
            <form @submit.prevent="searchInvoice">
                <div class="row mb-3">
                    <div class="col-lg-4">
                        <div class="input-group">
                            <input type="text" class="form-control" placeholder="Sök fakturor" v-model="invoice_search">
                            <div class="input-group-append">
                                <button type="button" class="btn btn-primary" @click="searchInvoice">Sök</button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>

            <div
                class="card"
                v-show="has_invoices_to_accept"
            >
                <div class="card-header header-elements-inline bg-white">
                    <h5 class="card-title">Ej godkända utskrifter</h5>
                    <div class="header-elements">
                        <button class="btn btn-light" @click="markAllInvoices">{{ (all_marked_invoices ? 'Avmarkera' : 'Markera') }} alla</button>
                    </div>
                </div>
                <rednax-datatable
                    :url="'/vue/invoices?is_accepted=0'"
                    :columns="invoice_columns"
                    sort-by="InvoiceDate"
                    sort-order="desc"
                    @rowclick="rowClick"
                    no-wrapper
                    @data:loaded="setHasInvoices"
                    ref="tblinvoices"
                >
                    <template #cell_InvoiceID="{ row }">
                        <router-link :to="{ name: 'invoice.edit', params: { id: row.InvoiceID } }">{{ row.ocr }}</router-link>
                    </template>
                    <template #cell_OrderID="{ row }">
                        <router-link :to="{ name: 'cardorders.edit', params: { id: row.OrderID } }">{{ row.OrderID }}</router-link>
                    </template>
                    <template #cell_CompanyName="{ row }">
                        <router-link :to="{ name: 'customers.edit', params: { id: row.CustomerID } }">{{ row.CompanyName }}</router-link>
                    </template>
                    <template #cell_InvoiceSumFull="{ row }">
                        {{ formatNumber(row.InvoiceSumFull, 2) }} {{ (row.CurrencyShort ? row.CurrencyShort : 'SEK') }}
                    </template>
                    <template #cell_Status="{ row }">
                        {{ ['Ny', 'Utskriven', 'Betald'][row.Status] }}
                    </template>
                    <template #cell_checkbox="{ row }">
                        <input type="checkbox" :name="'chk-reprint-' + row.InvoiceID" :true-value="1" :false-value="0" v-model="reprint[row.InvoiceID]">
                    </template>
                    <template #footer >
                        <div class="d-flex">
                            <button type="button" class="btn btn-primary ml-auto" @click="reGenInvoices(Object.keys(reprint).filter((key) => reprint[key]))">Skriv ut igen</button>
                        </div>
                    </template>
                </rednax-datatable>
            </div>

            <div
                class="card"
                v-show="has_reminders_to_accept"
            >
                <div class="card-header header-elements-inline bg-white">
                    <h5 class="card-title">Ej godkända påminnelser</h5>
                    <div class="header-elements">
                        <button class="btn btn-light" @click="markAllReminders">{{ (all_marked_reminders ? 'Avmarkera' : 'Markera') }} alla</button>
                    </div>
                </div>
                <rednax-datatable
                    :url="'/vue/invoices?is_accepted_reminder=0&is_paid=0'"
                    :columns="invoice_columns"
                    sort-by="InvoiceDate"
                    sort-order="desc"
                    @rowclick="rowClick"
                    no-wrapper
                    @data:loaded="setHasReminders"
                    ref="tblreminders"
                >
                    <template #cell_InvoiceID="{ row }">
                        <router-link :to="{ name: 'invoice.edit', params: { id: row.InvoiceID } }">{{ row.ocr }}</router-link>
                    </template>
                    <template #cell_OrderID="{ row }">
                        <router-link :to="{ name: 'cardorders.edit', params: { id: row.OrderID } }">{{ row.OrderID }}</router-link>
                    </template>
                    <template #cell_CompanyName="{ row }">
                        <router-link :to="{ name: 'customers.edit', params: { id: row.CustomerID } }">{{ row.CompanyName }}</router-link>
                    </template>
                    <template #cell_InvoiceSumFull="{ row }">
                        {{ formatNumber(row.InvoiceSumFull, 2) }} {{ (row.CurrencyShort ? row.CurrencyShort : 'SEK') }}
                    </template>
                    <template #cell_Status="{ row }">
                        {{ ['Ny', 'Utskriven', 'Betald'][row.Status] }}
                    </template>
                    <template #cell_checkbox="{ row }">
                        <input type="checkbox" :name="'chk-reprint_reminders-' + row.InvoiceID" :true-value="1" :false-value="0" v-model="reprint_reminders[row.InvoiceID]">
                    </template>
                    <template #footer >
                        <div class="d-flex">
                            <button type="button" class="btn btn-primary ml-auto" @click="reGenInvoices(Object.keys(reprint_reminders).filter((key) => reprint_reminders[key]), true)">Skriv ut igen</button>
                        </div>
                    </template>
                </rednax-datatable>
            </div>

            <rednax-datatable
                :url="'/vue/cardorders/list?status_id=invoicing&invoicing_company=supekort'"
                :columns="order_columns"
                sort-by="CreatedTS"
                sort-order="desc"
                @rowclick="rowClick"
                ref="tblorders"
            >
                <template #header>
                    <h5 class="card-title">Ej fakturerade ordrar</h5>
                    <div class="header-elements">
                        <button class="btn btn-light" @click="markAllOrders">{{ (all_marked_orders ? 'Avmarkera' : 'Markera') }} alla</button>
                    </div>
                </template>
                <template #cell_OrderID="{ row }">
                    <router-link :to="{ name: 'cardorders.edit', params: { id: row.OrderID } }">{{ row.OrderID }}</router-link>
                </template>
                <template #cell_CreatedTS="{ row }">
                    {{ formatDateTime(row.CreatedTS).substring(0, 10) }}
                </template>
                <template #cell_DeliveryDate="{ row }">
                    {{ formatDateTime(row.DeliveryDate).substring(0, 10) }}
                </template>
                <template #cell_OrderSum="{ row }">
                    {{ formatNumber(row.OrderSum, 2) }} {{ (row.CurrencyShort ? row.CurrencyShort : 'SEK') }}
                </template>
                <template #cell_CompanyName="{ row }">
                    <router-link :to="{ name: 'customers.edit', params: { id: row.CustomerID } }">{{ row.CompanyName }}</router-link>
                </template>
                <template #cell_checkbox="{ row }">
                    <input type="checkbox" :name="'chk-create_invoices-' + row.OrderID" :true-value="1" :false-value="0" v-model="create_invoices[row.OrderID]">
                </template>
                <template #cell_PaymentMethod="{ row }">
                    {{ payment_methods[row.PaymentMethod] ? payment_methods[row.PaymentMethod].label : row.PaymentMethod }}
                </template>
                <template #cell_PaymentStatusID="{ row }">
                    <span class="badge" :class="['badge-' + (payment_statuses[row.PaymentStatusID] ? payment_statuses[row.PaymentStatusID].color : 'secondary')]">
                        {{ payment_statuses[row.PaymentStatusID] ? payment_statuses[row.PaymentStatusID].label : row.PaymentStatusID }}
                    </span>
                </template>
                <template #cell_DeliveryStatusID="{ row }">
                    <span class="badge" :class="['badge-' + (delivery_statuses[row.DeliveryStatusID] ? delivery_statuses[row.DeliveryStatusID].color : 'secondary')]">
                        {{ delivery_statuses[row.DeliveryStatusID] ? delivery_statuses[row.DeliveryStatusID].label : row.DeliveryStatusID }}
                    </span>
                </template>
                <template #cell_StatusID="{ row }">
                    <span v-if="row.StatusID === 750" class="badge" :class="['badge-' + (delivery_statuses[row.DeliveryStatusID] ? delivery_statuses[row.DeliveryStatusID].color : 'secondary')]">
                        {{ delivery_statuses[row.DeliveryStatusID] ? delivery_statuses[row.DeliveryStatusID].label : row.DeliveryStatusID }}
                    </span>
                    <span v-else class="badge" :class="['badge-' + (order_statuses[row.StatusID] ? order_statuses[row.StatusID].color : 'secondary')]">
                        {{ (order_statuses[row.StatusID] ? order_statuses[row.StatusID].label : row.StatusID) }}
                    </span>
                </template>
                <template #footer >
                    <div class="d-flex">
                        <button type="button" class="btn btn-primary ml-auto" @click="createInvoices(Object.keys(create_invoices).filter((key) => create_invoices[key]))">Skapa fakturor</button>
                    </div>
                </template>
            </rednax-datatable>

            <div
                class="card"
                v-show="has_np_orders"
            >
                <div class="card-header header-elements-inline bg-white">
                    <h5 class="card-title">Ej fakturerade Newport-ordrar</h5>
                    <div class="header-elements">
                        <button class="btn btn-light" @click="markAllNpOrders">{{ (all_marked_nporders ? 'Avmarkera' : 'Markera') }} alla</button>
                    </div>
                </div>
                <rednax-datatable
                    :url="'/vue/cardorders/list?status_id=invoicing&invoicing_company=newport'"
                    :columns="order_columns"
                    sort-by="CreatedTS"
                    sort-order="desc"
                    @rowclick="rowClick"
                    no-wrapper
                    @data:loaded="setHasNpOrders"
                    ref="tblnporders"
                >
                    <template #cell_OrderID="{ row }">
                        <router-link :to="{ name: 'cardorders.edit', params: { id: row.OrderID } }">{{ row.OrderID }}</router-link>
                    </template>
                    <template #cell_CreatedTS="{ row }">
                        {{ formatDateTime(row.CreatedTS).substring(0, 10) }}
                    </template>
                    <template #cell_DeliveryDate="{ row }">
                        {{ formatDateTime(row.DeliveryDate).substring(0, 10) }}
                    </template>
                    <template #cell_OrderSum="{ row }">
                        {{ formatNumber(row.OrderSum, 2) }} {{ (row.CurrencyShort ? row.CurrencyShort : 'SEK') }}
                    </template>
                    <template #cell_CompanyName="{ row }">
                        <router-link :to="{ name: 'customers.edit', params: { id: row.CustomerID } }">{{ row.CompanyName }}</router-link>
                    </template>
                    <template #cell_checkbox="{ row }">
                        <input type="checkbox" :name="'chk-create_nph_invoices-' + row.OrderID" :true-value="1" :false-value="0" v-model="create_nph_invoices[row.OrderID]">
                    </template>
                    <template #cell_PaymentStatusID="{ row }">
                        <span class="badge" :class="['badge-' + (payment_statuses[row.PaymentStatusID] ? payment_statuses[row.PaymentStatusID].color : 'secondary')]">
                            {{ payment_statuses[row.PaymentStatusID] ? payment_statuses[row.PaymentStatusID].label : row.PaymentStatusID }}
                        </span>
                    </template>
                    <template #cell_DeliveryStatusID="{ row }">
                        <span class="badge" :class="['badge-' + (delivery_statuses[row.DeliveryStatusID] ? delivery_statuses[row.DeliveryStatusID].color : 'secondary')]">
                            {{ delivery_statuses[row.DeliveryStatusID] ? delivery_statuses[row.DeliveryStatusID].label : row.DeliveryStatusID }}
                        </span>
                    </template>
                    <template #cell_StatusID="{ row }">
                        <span class="badge" :class="['badge-' + (order_statuses[row.StatusID] ? order_statuses[row.StatusID].color : 'secondary')]">
                            {{ (order_statuses[row.StatusID] ? order_statuses[row.StatusID].label : row.StatusID) }}
                        </span>
                    </template>
                    <template #footer >
                        <div class="d-flex">
                            <button type="button" class="btn btn-primary ml-auto">Skapa fakturor I det grå</button>
                        </div>
                    </template>
                </rednax-datatable>
            </div>
        </div> <!-- /content -->
    </div>
</template>

<script>
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import RednaxDatatable from '@components/Datatable/RednaxDatatableComponent';
import { formatDateTime, formatNumber } from '@services/Helpers';
import BackendRepository from '@services/repositories/BackendRepository';
import { keyBy } from 'lodash';

export default {
    components: {
        LimitlessPageHeader,
        RednaxDatatable,
    },
    data: () => ({
        invoice_search: '',
        table_filter: {
            search: '',
            is_insecure: null,
            is_bankrupt: null,
            is_credit: null,
            is_paid: null,
            is_late: null,
        },
        reminder: {},
        status_options: [
            {
                id: null,
                label: 'Inkl. ',
            },
            {
                id: 1,
                label: 'Endast',
            },
            {
                id: 0,
                label: 'Dölj',
            },
        ],
        reprint: {},
        reprint_reminders: {},
        create_invoices: {},
        create_nph_invoices: {},
        has_invoices_to_accept: false,
        has_reminders_to_accept: false,
        has_np_orders: false,
        all_marked_invoices: false,
        all_marked_reminders: false,
        all_marked_orders: false,
        all_marked_nporders: false,
        order_statuses: [],
        delivery_statuses: [],
        payment_statuses: [],
        payment_methods: [],
    }),
    mounted() {
        this.getStatuses();
    },
    methods: {
        formatDateTime,
        formatNumber,
        getStatuses() {
            BackendRepository.getCardOrderPaymentStatuses().then((res) => {
                this.payment_statuses = keyBy(res, 'id');
            });
            BackendRepository.getCardOrderDeliveryStatuses().then((res) => {
                this.delivery_statuses = keyBy(res, 'id');
            });
            BackendRepository.getCardOrderOrderStatuses().then((res) => {
                this.order_statuses = keyBy(res, 'id');
            });
            BackendRepository.getCardOrderPaymentMethods().then((res) => {
                this.payment_methods = keyBy(res, 'id');
            });
        },
        rowClick(row, event) {
            if (event.metaKey === true || event.ctrlKey === true) {
                window.open('/cardseries/' + row.CardSeriesID).focus();
            } else {
                this.$router.push({
                    name: 'invoice.edit',
                    params: { id: row.InvoiceID },
                });
            }
        },
        async createInvoices(orderIds) {
            await this.$router.push({
                name: 'invoice.preview',
                query: {
                    order_ids: orderIds,
                },
            });
        },
        async reGenInvoices(invoiceIds, reminder = false) {
            await this.$router.push({
                name: 'invoice.preview',
                query: {
                    invoice_ids: invoiceIds,
                    reminder,
                },
            });
        },
        async searchInvoice() {
            await this.$router.push({
                name: 'invoice.list',
                query: {
                    search: this.invoice_search,
                },
            });
        },
        setHasInvoices(data) {
            if (data.data.length > 0) {
                this.has_invoices_to_accept = true;
            }
        },
        setHasReminders(data) {
            if (data.data.length > 0) {
                this.has_reminders_to_accept = true;
            }
        },
        setHasNpOrders(data) {
            if (data.data.length > 0) {
                this.has_np_orders = true;
            }
        },
        markAllInvoices() {
            this.all_marked_invoices = !this.all_marked_invoices;
            this.$refs.tblinvoices.local_data.data.forEach((row) => {
                this.$set(this.reprint, row.InvoiceID, (this.all_marked_invoices ? 1 : 0));
            });
        },
        markAllReminders() {
            this.all_marked_reminders = !this.all_marked_reminders;
            this.$refs.tblreminders.local_data.data.forEach((row) => {
                this.$set(this.reprint_reminders, row.InvoiceID, (this.all_marked_reminders ? 1 : 0));
            });
        },
        markAllOrders() {
            this.all_marked_orders = !this.all_marked_orders;
            this.$refs.tblorders.local_data.data.forEach((row) => {
                this.$set(this.create_invoices, row.OrderID, (this.all_marked_orders ? 1 : 0));
            });
        },
        markAllNpOrders() {
            this.all_marked_nporders = !this.all_marked_nporders;
            this.$refs.tblnporders.local_data.data.forEach((row) => {
                this.$set(this.create_nph_invoices, row.OrderID, (this.all_marked_nporders ? 1 : 0));
            });
        },
    },
    computed: {
        invoice_columns() {
            return [
                {
                    header: 'OCR',
                    data: 'InvoiceID',
                    sortable: true,
                },
                {
                    header: 'OrderNr',
                    data: 'OrderID',
                    sortable: true,
                },
                {
                    header: 'Kund',
                    data: 'CompanyName',
                    sortable: true,
                },
                {
                    header: 'Fakturadatum',
                    data: 'InvoiceDate',
                    sortable: true,
                },
                {
                    header: 'Förfallodatum',
                    data: 'ExpireDate',
                    sortable: true,
                },
                {
                    header: 'Fakturabelopp',
                    data: 'InvoiceSumFull',
                    sortable: true,
                    align_right: true,
                },
                {
                    header: 'Status',
                    data: 'Status',
                    sortable: true,
                },
                {
                    header: 'Granska',
                    data: 'checkbox',
                    sortable: false,
                    clickable: false,
                    align_right: true,
                },
            ];
        },
        order_columns() {
            return [
                {
                    header: 'OrderNr',
                    data: 'OrderID',
                    sortable: true,
                },
                {
                    header: 'Orderdatum',
                    data: 'CreatedTS',
                    sortable: true,
                },
                {
                    header: 'Lev.datum',
                    data: 'DeliveryDate',
                    sortable: true,
                },
                {
                    header: 'Kund',
                    data: 'CompanyName',
                    sortable: true,
                },
                {
                    header: 'Ordervärde',
                    data: 'OrderSum',
                    sortable: true,
                    align_right: true,
                },
                {
                    header: 'Betalsätt',
                    data: 'PaymentMethod',
                    sortable: true,
                },
                {
                    header: 'Betalstatus',
                    data: 'PaymentStatusID',
                    sortable: true,
                },
                {
                    header: 'Orderstatus',
                    data: 'StatusID',
                    sortable: false,
                },
                {
                    header: 'Fakturera',
                    data: 'checkbox',
                    sortable: false,
                    clickable: false,
                    align_right: true,
                },
            ];
        },
    },
};

</script>
